'use client'

import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import {
  SUBSCRIPTION_LISTS,
  SUBSCRIPTION_PRIOR_UNSUBSCRIBE_NOTE,
  SUBSCRIPTION_REQUEST_HEADING,
} from '@/lib/subscriptionConsent'
import { track } from '@/utils/analytics'
import { fetchWithTimeout } from '@/utils/fetchWithTimeout'

/**
 * The site's one email capture form.
 *
 * ── What the research says, and what it means here ──────────────────────────
 *
 * · One field. Every additional field costs 10–30% of conversions, and a name
 *   buys nothing we would ever use — the emails are not personalised.
 * · No full-screen interstitial anywhere. Google penalises intrusive mobile
 *   interstitials and this site lives entirely on organic search, so the
 *   highest-converting popup format is the one format we cannot use. The
 *   slide-in variant stays well under 30% of the viewport for that reason.
 * · Contextual beats generic. A form that appears at the moment someone wanted
 *   something we did not have outperforms a sitewide "subscribe" box by a wide
 *   margin, because the offer answers the need they just expressed.
 * · Consent has to be demonstrable. The exact wording under the button is
 *   stored on the row with the address, so what someone agreed to is
 *   recoverable later rather than assumed.
 *
 * ── The offer ───────────────────────────────────────────────────────────────
 * Not "subscribe to our newsletter", which is a request for a favour. This
 * list promises the one thing the site is actually built to deliver and
 * competitors structurally cannot: you are told when a score changes and when
 * a study we relied on gets challenged. It is the corrections policy turned
 * into a product.
 */

export type SubscribeSource =
  | 'barcode_miss'
  | 'product_miss'
  | 'scroll_prompt'
  | 'footer'
  | 'newsletter_page'
  | 'product_page'
  | 'methodology'

export type SubscribeVariant = 'panel' | 'inline' | 'compact'

/**
 * Domain typo suggestions.
 *
 * This matters far more under a collect-now-send-later plan than it would
 * normally. An address typed as "gmial.com" today is not discovered until the
 * first send months from now, at which point it is a hard bounce — arriving in
 * a big batch, from a brand-new sending domain, on its very first send. That
 * combination is the fastest way to land permanently in spam folders.
 *
 * Catching it at the keyboard costs nothing. This SUGGESTS rather than
 * rejects: the correction is one click and an unusual-but-real domain is never
 * blocked. Server-side we store exactly what was typed.
 */
const COMMON_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'hotmail.com',
  'hotmail.co.uk', 'outlook.com', 'live.com', 'icloud.com', 'me.com',
  'aol.com', 'proton.me', 'protonmail.com', 'msn.com', 'comcast.net',
]

/** Levenshtein, bounded — we only care about distances of 1 or 2. */
function editDistance(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 2) return 99
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let last = prev[0]
    prev[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j]
      prev[j] = Math.min(
        prev[j] + 1,
        prev[j - 1] + 1,
        last + (a[i - 1] === b[j - 1] ? 0 : 1)
      )
      last = tmp
    }
  }
  return prev[b.length]
}

function suggestDomain(email: string): string | null {
  const at = email.lastIndexOf('@')
  if (at < 1) return null
  const local = email.slice(0, at)
  const domain = email.slice(at + 1).toLowerCase()
  if (!domain || COMMON_DOMAINS.includes(domain)) return null

  for (const candidate of COMMON_DOMAINS) {
    const d = editDistance(domain, candidate)
    // Distance 1 is almost always a typo. Distance 2 only on longer domains,
    // where the false-positive risk is lower ("gmai.co" vs "gmail.com").
    if (d === 1 || (d === 2 && candidate.length > 8)) return `${local}@${candidate}`
  }
  return null
}

interface Props {
  source: SubscribeSource
  /** What they were looking at — a barcode, a product slug, a search term. */
  context?: string
  variant?: SubscribeVariant
  heading?: string
  blurb?: string
  cta?: string
  /** Rendered on dark backgrounds (the product hero, the slide-in). */
  tone?: 'light' | 'dark'
  /** Stable offer identifier, independent of placement, for offer-level CVR. */
  offerId?: string
  onSuccess?: () => void
}

export default function SubscribeForm({
  source,
  context,
  variant = 'panel',
  heading = 'Know what changed—and what to swap next',
  blurb = 'A short monthly brief with corrected scores, newly reviewed products, and one practical lower-contact swap. No daily tips, and every email says where you signed up.',
  cta = 'Send me the brief',
  tone = 'light',
  offerId = 'monthly_evidence_brief_v2',
  onSuccess,
}: Props) {
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const impressionTrackedRef = useRef(false)

  const dark = tone === 'dark'

  useEffect(() => {
    const form = formRef.current
    if (!form || impressionTrackedRef.current) return

    const fire = () => {
      if (impressionTrackedRef.current) return
      impressionTrackedRef.current = true
      track('email_capture_impression', {
        cta_surface: `newsletter_${source}`,
        offer_id: offerId,
        form_variant: variant,
        has_context: Boolean(context),
      })
    }

    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: [0.5] },
    )
    observer.observe(form)
    return () => observer.disconnect()
  }, [context, offerId, source, variant])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (state === 'sending') return
    setState('sending')
    setMessage('')

    try {
      const res = await fetchWithTimeout('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, context, website: honeypot }),
      }, 10_000)
      const data = await res.json()

      if (!data.ok) {
        setState('error')
        setMessage(data.message ?? 'That did not go through. Try again in a moment.')
        track('email_capture_error', {
          cta_surface: `newsletter_${source}`,
          offer_id: offerId,
          form_variant: variant,
          failure_stage: 'response',
        })
        return
      }

      setState('done')
      track('email_capture_submit', {
        cta_surface: `newsletter_${source}`,
        has_context: Boolean(context),
        offer_id: offerId,
        form_variant: variant,
      })
      onSuccess?.()
    } catch {
      setState('error')
      setMessage('That did not go through. Check your connection and try again.')
      track('email_capture_error', {
        cta_surface: `newsletter_${source}`,
        offer_id: offerId,
        form_variant: variant,
        failure_stage: 'network',
      })
    }
  }

  // Confirm the action, then say what happens next and how often, because no
  // welcome email follows and this box is the only confirmation they get. The
  // frequency and topics repeat the consent line they just agreed to.
  if (state === 'done') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={`flex items-start gap-3 rounded-2xl border p-5 ${
          dark ? 'border-cream/20 bg-cream/[0.07]' : 'border-forest/30 bg-forest/[0.06]'
        }`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`mt-0.5 h-6 w-6 shrink-0 ${dark ? 'text-goldLight' : 'text-forest'}`}
        >
          <path
            fillRule="evenodd"
            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
            clipRule="evenodd"
          />
        </svg>
        <div className="min-w-0">
          <p className={`font-display text-lg font-bold leading-snug ${dark ? 'text-cream' : 'text-ink'}`}>
            {SUBSCRIPTION_REQUEST_HEADING}
          </p>
          <p className={`mt-1 text-sm leading-relaxed ${dark ? 'text-cream/80' : 'text-gray-700'}`}>
            Expect a short brief, usually no more than once a month, covering new scores,
            corrections and evidence changes. You don&apos;t need to confirm anything.
          </p>
          <p className={`mt-2.5 text-xs leading-relaxed ${dark ? 'text-cream/60' : 'text-gray-500'}`}>
            {SUBSCRIPTION_PRIOR_UNSUBSCRIBE_NOTE}
          </p>
        </div>
      </div>
    )
  }

  const compact = variant === 'compact'

  const field = (
    <>
      {/* Honeypot. Hidden from people, visible to bots, never submitted to the
          database — see the route handler. */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-px w-px overflow-hidden">
        <label htmlFor={`website-${source}`}>Leave this empty</label>
        <input
          id={`website-${source}`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className={`flex gap-2 ${compact ? 'flex-row' : 'flex-col sm:flex-row'}`}>
        <label htmlFor={`email-${source}`} className="sr-only">
          Email address
        </label>
        <input
          id={`email-${source}`}
          name="email"
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            if (suggestion) setSuggestion(null)
          }}
          onBlur={(e) => setSuggestion(suggestDomain(e.target.value.trim()))}
          placeholder="you@example.com"
          className={`min-w-0 flex-1 rounded-xl border px-4 py-3 focus:outline-none focus-visible:ring-2 ${
            dark
              ? 'border-cream/25 bg-cream/[0.08] text-cream placeholder:text-cream/40 focus:border-goldLight focus-visible:ring-goldLight/40'
              : 'border-ink/15 bg-cream/50 text-ink placeholder:text-gray-400 focus:border-forest focus:bg-white focus-visible:ring-forest/40'
          }`}
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className={`shrink-0 rounded-xl px-5 py-3 font-medium transition-colors disabled:opacity-60 ${
            dark ? 'bg-goldLight text-deepForest hover:bg-goldLight/90' : 'bg-ink text-cream hover:bg-ink/90'
          }`}
        >
          {state === 'sending' ? 'Adding…' : cta}
        </button>
      </div>

      {suggestion && state !== 'sending' && (
        <p className={`mt-2 text-sm ${dark ? 'text-goldLight' : 'text-forest'}`}>
          Did you mean{' '}
          <button
            type="button"
            onClick={() => {
              setEmail(suggestion)
              setSuggestion(null)
            }}
            className="font-semibold underline underline-offset-2"
          >
            {suggestion}
          </button>
          ?
        </p>
      )}

      {state === 'error' && (
        <p
          className="mt-2 text-sm text-red-600"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {message}
        </p>
      )}

      {/* The consent line. This exact promise is stored alongside the address,
          so what someone agreed to is recoverable rather than assumed. */}
      <p className={`mt-2.5 text-xs leading-relaxed ${dark ? 'text-cream/55' : 'text-gray-500'}`}>
        {SUBSCRIPTION_LISTS.newsletter.consent}{' '}
        <Link
          href="/privacy"
          className={`underline ${dark ? 'text-cream/70' : 'text-gray-600'}`}
        >
          Privacy
        </Link>
      </p>
    </>
  )

  if (compact) {
    return (
      <form ref={formRef} onSubmit={submit} className="relative">
        {field}
      </form>
    )
  }

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className={`relative rounded-2xl border p-5 sm:p-6 ${
        dark
          ? 'border-cream/20 bg-cream/[0.06]'
          : variant === 'inline'
            ? 'border-ink/10 bg-white'
            : 'border-forest/25 bg-white shadow-sm'
      }`}
    >
      <p
        className={`text-xs font-semibold uppercase tracking-[0.2em] ${
          dark ? 'text-goldLight' : 'text-forest'
        }`}
      >
        The MicroPlastics email
      </p>
      <h2
        className={`mt-2 font-display text-xl font-bold leading-snug ${
          dark ? 'text-cream' : 'text-ink'
        }`}
      >
        {heading}
      </h2>
      <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-cream/70' : 'text-gray-600'}`}>
        {blurb}
      </p>
      <div className="mt-4">{field}</div>
    </form>
  )
}
